import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CheckboxModule } from 'primeng/checkbox';
import { FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';
import { PreEmploymentActivitiesService } from '../../../../../../../services/pre-employment-activities.service';

interface IdentityCardAttachment {
    key: string;
    presignedUrl: string;
}

@Component({
    selector: 'commerce-tax-relief',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        CheckboxModule,
        FileUploadModule,
        InputNumberModule,
        TooltipModule,
    ],
    templateUrl: './commerce-tax-relief.component.html',
    styleUrls: ['./commerce-tax-relief.component.scss'],
    standalone: true,
})
export class CommerceTaxReliefComponent implements OnInit {
    @Input() form: FormGroup;
    @Input() activityId: string | null | undefined;

    isUploading: boolean = false;
    uploadError: boolean = false;

    private readonly preEmploymentActivitiesService = inject(PreEmploymentActivitiesService);

    ngOnInit(): void {
        this.setupConditionalValidation();
    }

    get showIncomeSection(): boolean {
        return !this.form.get('reliefAcceptance')?.value || !this.form.get('nightOrHolidayWorkAcceptance')?.value;
    }

    get identityCardAttachments(): IdentityCardAttachment[] {
        const attachments: unknown = this.form.get('identityCardAttachments')?.value;

        return Array.isArray(attachments) ? attachments as IdentityCardAttachment[] : [];
    }

    get hasIdentityCardAttachment(): boolean {
        return this.identityCardAttachments.length > 0;
    }

    getAttachmentName(attachment: IdentityCardAttachment): string {
        return attachment.key.split('/').pop() ?? 'documento';
    }

    async onIdentityCardSelect(event: any): Promise<void> {
        const file: File | undefined = event.files?.[0];

        if (!file || !this.activityId) {
            this.uploadError = true;
            return;
        }

        this.isUploading = true;
        this.uploadError = false;

        try {
            const resource = await firstValueFrom(
                this.preEmploymentActivitiesService.getPresignedUrlForImageUpload(this.activityId),
            );
            const response: Response = await fetch(resource.presignedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            });

            if (!response.ok) {
                throw new Error('Identity document upload failed');
            }

            this.form.get('identityCardAttachments')?.setValue([resource]);
            this.form.get('identityCardAttachments')?.markAsDirty();
            this.form.get('identityCardAttachments')?.updateValueAndValidity();
        } catch {
            this.uploadError = true;
            this.form.get('identityCardAttachments')?.setValue([]);
            this.form.get('identityCardAttachments')?.updateValueAndValidity();
        } finally {
            this.isUploading = false;
        }
    }

    onIdentityCardRemove(): void {
        this.form.get('identityCardAttachments')?.setValue([]);
        this.form.get('identityCardAttachments')?.markAsDirty();
        this.form.get('identityCardAttachments')?.updateValueAndValidity();
        this.uploadError = false;
    }

    private setupConditionalValidation(): void {
        const incomeControl = this.form.get('income');
        const incomeAcceptanceControl = this.form.get('incomeAcceptance');
        const substituteTaxAcceptanceControl = this.form.get('alreadyTaxedSubstituteTaxAcceptance');
        const substituteTaxAmountControl = this.form.get('alreadyTaxedSubstituteTaxAmount');

        const updateIncomeValidation = (): void => {
            const isReliefRenounced = !!this.form.get('reliefAcceptance')?.value;
            const isNightOrHolidayWorkSelected = !!this.form.get('nightOrHolidayWorkAcceptance')?.value;
            const isIncomeAccepted = !!incomeAcceptanceControl?.value;
            const isSubstituteTaxAccepted = !!substituteTaxAcceptanceControl?.value;

            if (isReliefRenounced && isNightOrHolidayWorkSelected) {
                incomeControl?.setValue(null, { emitEvent: false });
                incomeAcceptanceControl?.setValue(false, { emitEvent: false });
                incomeAcceptanceControl?.clearValidators();
                incomeControl?.clearValidators();
            } else if (isIncomeAccepted) {
                incomeAcceptanceControl?.setValidators(Validators.requiredTrue);
                incomeControl?.setValidators([Validators.required, Validators.min(0)]);
            } else {
                incomeAcceptanceControl?.setValidators(Validators.requiredTrue);
                incomeControl?.setValue(null, { emitEvent: false });
                incomeControl?.clearValidators();
            }

            if (isReliefRenounced && isNightOrHolidayWorkSelected) {
                substituteTaxAcceptanceControl?.setValue(false, { emitEvent: false });
                substituteTaxAcceptanceControl?.clearValidators();
                substituteTaxAmountControl?.setValue(null, { emitEvent: false });
                substituteTaxAmountControl?.clearValidators();
            } else if (isSubstituteTaxAccepted) {
                substituteTaxAcceptanceControl?.setValidators(Validators.requiredTrue);
                substituteTaxAmountControl?.setValidators([Validators.required, Validators.min(0)]);
            } else {
                substituteTaxAcceptanceControl?.setValidators(Validators.requiredTrue);
                substituteTaxAmountControl?.setValue(null, { emitEvent: false });
                substituteTaxAmountControl?.clearValidators();
            }

            incomeControl?.updateValueAndValidity({ emitEvent: false });
            incomeAcceptanceControl?.updateValueAndValidity({ emitEvent: false });
            substituteTaxAcceptanceControl?.updateValueAndValidity({ emitEvent: false });
            substituteTaxAmountControl?.updateValueAndValidity({ emitEvent: false });
        };

        this.form.get('reliefAcceptance')?.valueChanges.subscribe(() => updateIncomeValidation());
        this.form.get('nightOrHolidayWorkAcceptance')?.valueChanges.subscribe(() => updateIncomeValidation());
        incomeAcceptanceControl?.valueChanges.subscribe(() => updateIncomeValidation());
        substituteTaxAcceptanceControl?.valueChanges.subscribe(() => updateIncomeValidation());

        updateIncomeValidation();
    }
}